import { clearKey } from '@plone/volto/actions/asyncConnect/asyncConnect';

/**
 * BL-0153: core's asyncConnect wrapper (helpers/AsyncConnect/index.js
 * `wrapWithDispatch`) stores every resolved promise a second time, under its
 * own key, in `state.reduxAsyncConnect` -- alongside the reducer slice a
 * dedicated reducer already populates for the same data: `GET_SITE` equals
 * `state.site.data`, and on a Document page `content` equals
 * `state.content.data`. That is 8-18 KB of `window.__data` per page, before
 * gzip, for a copy nothing reads.
 *
 * Confirmed dead, not assumed: the only place in core or this addon that
 * reads `state.reduxAsyncConnect[key]` at all is `finalMapStateToProps` in
 * helpers/AsyncConnect/index.js, which maps it into a same-named prop on
 * whichever component's own `asyncConnect([...])` list carries that key.
 * App.jsx is the only one with a `content` item, and its OWN later `connect()`
 * (`content: state.content.data`) sits after it in the same HOC chain, so
 * react-redux's default mergeProps (stateProps spread after ownProps)
 * overwrites the reduxAsyncConnect copy before App ever renders it. `GET_SITE`
 * reaches every asyncConnect-wrapped component the same way (core's
 * `getSiteAsyncPropExtender` has no `exact`, by design -- see
 * components/SiteHome/asyncData.js's header comment), and grepping the whole
 * tree for `GET_SITE`/`props.GET_SITE` turns up only the action-type constant
 * and the extender itself -- no component ever reads that prop. Site data
 * everywhere else comes from `state.site.data` (reducers/site/site.js),
 * populated by the same dispatch, independently of this wrapper.
 *
 * `clearKey(key)` (core, actions/asyncConnect/asyncConnect.js) sets
 * `state.reduxAsyncConnect[key] = null` and resets that key's OWN
 * `loadState[key].loaded` -- it does not touch the single
 * `state.reduxAsyncConnect.loaded` flag that AsyncConnect.jsx's `isLoaded()`
 * reads to decide whether to refetch on hydration (nothing in core or this
 * addon reads `loadState` outside of core's own tests). So dropping these two
 * keys cannot cause the "extra @site request in Chrome" the ticket warns
 * against as the failure sign.
 *
 * Wired as a `storeExtenders` middleware (core, config/index.js +
 * store.js) rather than a change to core's wrapper or Html.jsx: it watches
 * for core's own END_GLOBAL_LOAD (dispatched once, by `loadOnServer`, right
 * after SSR data-loading finishes -- helpers/AsyncConnect/ssr.js) and clears
 * the two known-duplicate keys immediately after, synchronously, before
 * server.jsx reads `store.getState()` to serialize `window.__data`. Guarded
 * to `__SERVER__` because the duplication this ticket measured is in the SSR
 * payload; a client-side store already has both keys refreshed by its own
 * per-navigation asyncConnect cycle, so there is nothing to save there.
 */
export const dedupeSsrStateExtender = (middlewareStack) => [
  ...middlewareStack,
  (store) => (next) => (action) => {
    const result = next(action);
    if (__SERVER__ && action.type === '@redux-conn/END_GLOBAL_LOAD') {
      store.dispatch(clearKey('GET_SITE'));
      store.dispatch(clearKey('content'));
    }
    return result;
  },
];

export default dedupeSsrStateExtender;
